import { collection, deleteDoc, doc, getDocs, query, setDoc, updateDoc, where } from 'firebase/firestore';
import { v4 as uuidv4 } from 'uuid';
import { orderBy } from 'lodash';

import {
  ADD_SURGEON,
  DISABLE_SURGEON,
  ENABLE_SURGEON,
  LOAD_DISTRIBUTORS,
  LOAD_SURGEONS,
  SET_SURGEONS,
  UPDATE_SURGEON,
  UPDATE_SURGEON_HOSPITALS,
} from './actionTypes';

import { DISTRIBUTORS_COLLECTION, SURGEONS_COLLECTION } from '../firebase/collections';
import Firebase from '../firebase';

import { sendEmail } from '../util';

import { roleAssignmentEmail } from '../constants/emailTemplates';
import { companyRoles, countryRoles, distributorRoles } from '../constants/userRoles';

const db = Firebase.db;
const DUPLICATE_SURGEON_ERROR = 'A surgeon with this user already exists for the selected distributor.';

const hasDuplicateSurgeonAssignment = (surgeons = [], surgeon = {}, currentId = null) => (
  surgeons.some((item) => (
    item.userId === surgeon.userId
    && item.distributor === surgeon.distributor
    && item.id !== currentId
  ))
);

export const setSurgeons = (surgeons) => ({ type: SET_SURGEONS, surgeons });

export const fetchSurgeons = () => async (dispatch, getState) => {
  const state = getState();
  const currentUser = state.user.currentUser;
  let distributors = state.distributors.list;

  dispatch({ type: LOAD_SURGEONS });

  const surgeonsCollection = collection(db, SURGEONS_COLLECTION);

  let q;

  if (companyRoles.includes(currentUser.role)) {
    q = query(
      surgeonsCollection,
      where('company', '==', currentUser.company)
    );
  } else {
    q = query(surgeonsCollection);
  }

  const snapshot = await getDocs(q);

  let surgeons = snapshot.docs.map((doc) => ({
    id: doc.id,
    ...doc.data(),
  }));

  if (countryRoles.includes(currentUser.role)) {
    if (!distributors.length) {
      distributors = await dispatch(getDistributors());
    }

    surgeons = surgeons.filter((s) => {
      const distributor = distributors.find((d) => d.id === s.distributor);
      return currentUser.administrationCountries?.includes(distributor?.country);
    });
  }

  if (distributorRoles.includes(currentUser.role)) {
    surgeons = surgeons.filter((s) => s.distributor === currentUser?.distributor || currentUser?.distributors?.includes(s.distributor));
  }

  dispatch(setSurgeons(orderBy(surgeons, 'name')));
};

export const getDistributors = () => async (dispatch, getState) => {
  const state = getState();
  const currentUser = state.user.currentUser;

  dispatch({ type: LOAD_DISTRIBUTORS });

  const distributorsCollection = collection(db, DISTRIBUTORS_COLLECTION);

  let q;

  if (companyRoles.includes(currentUser.role)) {
    q = query(
      distributorsCollection,
      where('company', '==', currentUser.company)
    );
  } else {
    q = query(distributorsCollection);
  }

  const snapshot = await getDocs(q);

  let distributors = snapshot.docs.map((doc) => ({
    id: doc.id,
    ...doc.data(),
  }));

  if (countryRoles.includes(currentUser.role)) {
    distributors = distributors.filter((d) => currentUser.administrationCountries?.includes(d.country));
  }

  return distributors;
};

export const createSurgeon = (surgeon) => (dispatch, getState) => {
  const surgeonsCollection = collection(db, SURGEONS_COLLECTION);
  const state = getState();
  const surgeons = state.surgeons.list || [];
  const users = state.users.list || [];
  const distributors = state.distributors.list || [];
  const notificationsSettings = state.settings.notifications;

  if (hasDuplicateSurgeonAssignment(surgeons, surgeon)) {
    throw new Error(DUPLICATE_SURGEON_ERROR);
  }

  const distributor = distributors?.find((d) => d.id === surgeon.distributor);
  const company = distributor?.company || '';

  const data = {
    id: uuidv4(),
    company,
    distributor: surgeon.distributor || null,
    engineer: surgeon.engineer || [],
    manufacturer: surgeon.manufacturer || null,
    manager: surgeon.manager || [],
    salesRep: surgeon.salesRep || [],
    registrar: surgeon.registrar || [],
    logistics: surgeon.logistics || null,
    hospitals: surgeon.hospitals || [],
    userId: surgeon.userId || null,
  };

  return Promise.all([
    setDoc(doc(surgeonsCollection, data.id), data),
  ]).then(() => {
    const recipient = users.find((user) => user.uid === surgeon.userId);

    if (notificationsSettings.active) {
      sendEmail(recipient, roleAssignmentEmail.subject, roleAssignmentEmail.message);
    }

    dispatch({ type: ADD_SURGEON, userId: surgeon.userId, surgeon, distributor: surgeon.distributor });
  });
};

export const disableSurgeon = (id) => (dispatch) => {
  const surgeonDoc = doc(db, SURGEONS_COLLECTION, id);

  dispatch({ type: LOAD_SURGEONS });

  return setDoc(surgeonDoc, { disabled: true }, { merge: true })
    .then(() => dispatch({ type: DISABLE_SURGEON, id }));
};

export const enableSurgeon = (id) => (dispatch) => {
  const surgeonDoc = doc(db, SURGEONS_COLLECTION, id);

  dispatch({ type: LOAD_SURGEONS });

  return setDoc(surgeonDoc, { disabled: false }, { merge: true })
    .then(() => dispatch({ type: ENABLE_SURGEON, id }));
};

export const updateSurgeon = (id, surgeon) => (dispatch, getState) => {
  const state = getState();
  const surgeons = state.surgeons.list || [];
  const distributors = state.distributors.list || [];

  if (hasDuplicateSurgeonAssignment(surgeons, surgeon, id)) {
    throw new Error(DUPLICATE_SURGEON_ERROR);
  }

  const surgeonDoc = doc(db, SURGEONS_COLLECTION, id);

  const distributor = distributors?.find((d) => d.id === surgeon.distributor);
  const company = distributor?.company || '';

  return updateDoc(surgeonDoc, { ...surgeon, company })
    .then(() => dispatch({ type: UPDATE_SURGEON, id, surgeon }));
};

export const updateHospitals = (id, hospitals) => (dispatch) => {
  const surgeonDoc = doc(db, SURGEONS_COLLECTION, id);

  dispatch({ type: UPDATE_SURGEON_HOSPITALS, id, hospitals });
  return updateDoc(surgeonDoc, { hospitals });
};

export const deleteSurgeon = (surgeonId) => (dispatch) => {
  const path = doc(db, SURGEONS_COLLECTION, surgeonId);
  return deleteDoc(path);
}
